import { FC, ReactElement } from 'react';
import { DialogContent, MenuItem, OutlinedInput, Select, Stack } from '@mui/material';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { ControlledFormField } from '../../../form/ControlledFormField';
import { FormField } from '../../../form/FormField';
import { FormErrors } from '../../../form/FormErrors';
import { MetricBehaviorSelect } from '../MetricBehaviorSelect';
import { useTypedForm } from '../../../../../shared/fe/hooks/useForm';
import { getBehaviorHintText } from '../utils/getHintTexts';

const createFormSchema = (intl: IntlShape) =>
	z.object({
		behavior: z.enum(['latest', 'average', 'sum', 'proportion']),
		precision: z.preprocess(
			(numeric) => parseInt(z.string().parse(numeric), 10),
			z.number().nonnegative(intl.formatMessage({ id: 'form.metric-template.error.invalid-precision' })),
		),
		unit: z.enum(['number', 'currency', 'percentage']),
		unitSign: z.string().optional(),
		expectations: z.enum(['increase', 'decrease']),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

type Props = Readonly<{
	stepper: ReactElement;
	actions: ReactElement;
	onSubmit: (values: FormValues) => void;
}>;

const CreateMetricStepSetup: FC<Props> = ({ stepper, actions, onSubmit }) => {
	const intl = useIntl();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		values: {
			behavior: 'latest',
			precision: '0',
			unit: 'number',
			unitSign: undefined,
			expectations: 'increase',
		},
	});

	const handleSubmit = (values: FormValues) => {
		onSubmit(values);
	};

	return (
		// @ts-ignore
		<form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
			<DialogContent>
				{stepper}

				<Stack spacing={3}>
					<ControlledFormField
						form={form}
						name={'behavior'}
						label={intl.formatMessage({ id: 'form.metric.label.behavior' })}
						description={intl.formatMessage({ id: 'form.metric.label.behavior.description' })}
						render={(props) => <MetricBehaviorSelect {...props} />}
						hint={getBehaviorHintText(intl, form.watch('behavior'))}
					/>

					<FormField
						form={form}
						name={'precision'}
						label={intl.formatMessage({ id: 'form.metric-template.label.precision' })}
						description={intl.formatMessage({ id: 'form.metric-template.label.precision.description' })}
						render={(props) => <OutlinedInput type={'number'} sx={{ width: 100 }} {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'unit'}
						label={intl.formatMessage({ id: 'form.metric-template.label.unit' })}
						description={intl.formatMessage({ id: 'form.metric-template.label.unit.description' })}
						render={(props) => (
							<Select {...props}>
								<MenuItem value={'number'}>
									<FormattedMessage id={'form.metric-template.label.number'} />
								</MenuItem>
								<MenuItem value={'currency'}>
									<FormattedMessage id={'form.metric-template.label.currency'} />
								</MenuItem>
								<MenuItem value={'percentage'}>
									<FormattedMessage id={'form.metric-template.label.percentage'} />
								</MenuItem>
							</Select>
						)}
					/>
					<FormField
						form={form}
						name={'unitSign'}
						label={intl.formatMessage({ id: 'form.metric-template.label.unit-sign' })}
						render={(props) => <OutlinedInput type={'text'} sx={{ width: 100 }} inputProps={{ min: 0, max: 10 }} {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'expectations'}
						label={intl.formatMessage({ id: 'form.metric-template.label.expectations' })}
						description={intl.formatMessage({ id: 'form.metric-template.label.expectations.description' })}
						render={(props) => (
							<Select {...props}>
								<MenuItem value={'increase'}>
									<FormattedMessage id={'form.metric-template.label.increase'} />
								</MenuItem>
								<MenuItem value={'decrease'}>
									<FormattedMessage id={'form.metric-template.label.decrease'} />
								</MenuItem>
							</Select>
						)}
					/>

					<FormErrors form={form} />
				</Stack>
			</DialogContent>
			{actions}
		</form>
	);
};

export { CreateMetricStepSetup, type FormValues as SubmitValuesCreateMetricStepSetup };
